import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { OrderControllerService } from '@app/api/order-service/api/orderController.service';
import { O2oFulfillmentControllerService } from '@app/api/order-service/api/o2oFulfillmentController.service';
import { AfterSalesReasonControllerService } from '@app/api/order-service/api/afterSalesReasonController.service';
import { AfterSalesReason } from '@app/api/order-service/model/afterSalesReason';
import { CustomerReceiveConfirmRequestDto } from '@app/api/order-service/model/customerReceiveConfirmRequestDto';
import { O2oAuditRequestDto } from '@app/api/order-service/model/o2oAuditRequestDto';
import { O2oCancelRequestDto } from '@app/api/order-service/model/o2oCancelRequestDto';
import { O2oCancelResultVO } from '@app/api/order-service/model/o2oCancelResultVO';
import { SearchOrderEsParam } from '@app/api/order-service/model/searchOrderEsParam';
import { PageInfoOrderPageVo } from '@app/api/order-service/model/pageInfoOrderPageVo';
import {
  O2OInternalModel,
  O2OInternalSearchParams,
  O2OInternalResponse,
} from './o2o-internal.model';
import { cleanAddressString } from '@app/shared/utils/string-formatter.util';

/** sord_after_sales_reason.type discriminator for cancellation reasons (V033). */
const CANCEL_REASON_TYPE = 100;

@Injectable({
  providedIn: 'root',
})
export class O2OInternalService {
  private orderService = inject(OrderControllerService);
  private o2oFulfillmentService = inject(O2oFulfillmentControllerService);
  private afterSalesReasonService = inject(AfterSalesReasonControllerService);

  /** POST /v1/order/{orderId}/o2o/cancel */
  cancelO2oOrder(
    orderId: number,
    body: O2oCancelRequestDto,
  ): Observable<O2oCancelResultVO> {
    // Same Accept-header workaround as listCancelReasons() above: without an explicit
    // 'application/json' Accept, Configuration.isJsonMime('*/*') is false and Angular reads
    // both the success AND error response bodies as a Blob, so a 400's message never reaches
    // the component's error handler.
    return this.o2oFulfillmentService.cancel(orderId, body, 'body', false, {
      httpHeaderAccept: 'application/json',
    } as any);
  }

  /** GET /v1/after-sales-reasons/list?type=100 */
  listCancelReasons(): Observable<AfterSalesReason[]> {
    // Backend doesn't declare `produces`, so the generated client's default Accept ('*/*')
    // isn't recognized as JSON by Configuration.isJsonMime — Angular then reads the response
    // as a Blob instead of parsing it, and the array silently becomes `{}`. Force JSON explicitly
    // (same workaround already used for orderPage1() in getOrders() above).
    return this.afterSalesReasonService.list13(CANCEL_REASON_TYPE, 'body', false, {
      httpHeaderAccept: 'application/json',
    } as any);
  }

  /** PATCH /v1/order/{orderId}/audit */
  auditO2oOrder(
    orderId: number,
    body: O2oAuditRequestDto,
  ): Observable<unknown> {
    return this.orderService.auditO2oOrder(orderId, body, 'body', false);
  }

  /** POST /v1/order/{orderId}/confirm-customer-receive */
  confirmCustomerReceive(
    orderId: number,
    body: CustomerReceiveConfirmRequestDto,
  ): Observable<unknown> {
    return this.orderService.confirmCustomerReceive(orderId, body, 'body', false);
  }

  getOrders(params: O2OInternalSearchParams): Observable<O2OInternalResponse> {
    // Convert O2O search params to API search params
    const searchParams: SearchOrderEsParam = {
      page: Math.floor((params.first || 0) / (params.rows || 20)),
      size: params.rows || 20,
      businessType: params.businessType || 'O2O',
      orderNo: params.orderNumber,
      buyerNick: params.buyerNickname,
      receiverName: params.receiverName,
      mobile: params.mobile, // Support both fields
      buyerRemarks: params.buyerNotes,
      serviceRemarks: params.merchantNotes,
      isGift:
        params.isGift === 'yes'
          ? true
          : params.isGift === 'no'
          ? false
          : undefined,
      orderStartTime: params.thirdPartyOrderTimeStart,
      orderEndTime: params.thirdPartyOrderTimeEnd,
      payStartTime: params.paymentTimeStart,
      payEndTime: params.paymentTimeEnd,
      creationStartDate: params.internalCreationTimeStart,
      creationEndDate: params.internalCreationTimeEnd,
      deliveryStartTime: params.shipmentTimeStart,
      deliveryEndTime: params.shipmentTimeEnd,
      receiveStatus: params.receiptStatus,
      channel: params.salesChannel,
      storeIdList: this.processStoreIdList(params.salesStore),
      tags: params.tags ? [params.tags] : undefined,
      stockHoldTimeoutOnly:
        params.stockHoldTimeoutOnly === 'yes' ? true : undefined,
    };

    return this.orderService
      .orderPage1(searchParams, 'body', false, {
        httpHeaderAccept: 'application/json',
      } as any)
      .pipe(
        // Transform API response to O2O format with optimized mapping
        map((response: PageInfoOrderPageVo) => {
          const pageContent = response.pageContent || [];
          const o2oOrders: O2OInternalModel[] = pageContent.map((order) =>
            this.transformOrderToO2O(order)
          );
          return {
            data: o2oOrders,
            total: response.totalElements || 0,
            success: true,
          };
        })
      );
  }

  /**
   * Multiselect may emit ids, numeric strings, or option objects `{ value, label }`.
   * Empty selection is omitted (`undefined`) so the API does not send `storeIdList: []`,
   * which would match no orders after data-scope tightening.
   */
  private processStoreIdList(salesStore?: unknown): number[] | undefined {
    return toO2oStoreIdList(salesStore);
  }

  /**
   * Optimized order transformation method
   */
  private transformOrderToO2O(order: any): O2OInternalModel {
    return {
      id: order.id?.toString() || '',
      salesStore: order.storeName || '',
      thirdPartyOrderNumber: order.outerOrderNo || '',
      salesChannel: order.channel || '',
      internalOrderNumber: order.innerOrderNo || '',
      salesEmployee: order.salesEmployeeRealName || '',
      tags: order.tagsList?.join(', ') || '',
      pickupStore: order.takingDeliveryStoreName || '',
      orderSource: order.actionType || '',
      quantity: order.goodsQuatity || 0,
      buyerNickname: order.buyerNick || '',
      buyerNotes: order.buyerRemarks || '',
      merchantNotes: order.orderRemarks || '',
      pickupMethod: order.takingDeliveryWay || '',
      receiverName: order.receiverName || '',
      receiverPhone: order.mobile || '',
      receiverAddress: cleanAddressString(order.address),
      orderStatus: order.orderStatus || '',
      fulfillmentStatus: order.fulfillmentStatus ?? '',
      payStatus: order.payStatus || 0,
      // Delivery_status column dropped: send progress lives on the fulfillment machine now.
      deliveryStatus: order.fulfillmentStatus ?? '',
      receivingStatus: order.receivingStatus || '',
      payType: order.payType || '',
      totalProductAmount: order.totalAmount || 0,
      buyerPayable: order.buyerPay || 0,
      totalProductCost: order.costPrice || 0,
      thirdPartyOrderTime: order.orderTime || '',
      paymentTime: order.payTime || '',
      internalCreationTime: order.creationDate || '',
      giftStatus: order.isGift || false,
      nextActions: order.nextActions,
      stockHoldCategory: order.stockHoldCategory || '',
      stockHoldExpiryTime: order.stockHoldExpiryTime || '',
      stockHoldTimeoutMinutes: order.stockHoldTimeoutMinutes,
      failureReasonDescription: order.failureReasonDescription || '',
      cancelReasonCode: order.cancelReasonCode || '',
    };
  }
}

export function toO2oStoreIdList(salesStore?: unknown): number[] | undefined {
  const values = Array.isArray(salesStore)
    ? salesStore
    : salesStore == null || salesStore === ''
      ? []
      : [salesStore];

  const ids = values
    .map((item) => toStoreId(item))
    .filter((id): id is number => id != null);

  return ids.length > 0 ? ids : undefined;
}

function toStoreId(item: unknown): number | undefined {
  if (item == null || item === '') {
    return undefined;
  }
  if (typeof item === 'number' && Number.isFinite(item)) {
    return item;
  }
  if (typeof item === 'string' && item.trim() !== '') {
    const parsed = Number(item);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  if (typeof item === 'object') {
    const record = item as Record<string, unknown>;
    return toStoreId(record['value'] ?? record['id'] ?? record['storeId']);
  }
  return undefined;
}
